/**
 * Iter 78 — Executive Notification Center
 *
 * Full-page command center for executive leadership:
 *   • Health / posture banner (from `/counts`)
 *   • Filters (category · severity · status · search)
 *   • Notifications list sorted by priority (deep-links + lifecycle actions)
 *   • Bulk actions: Scan Now, Mark All Read
 *
 * Every category / severity / status list comes from the API — nothing
 * is hard-coded on the frontend so the backend stays the single source
 * of truth (`catalog.py`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, RefreshCw, Loader2, Search, ExternalLink, ShieldAlert,
  ShieldCheck, Activity, CheckCheck, PlayCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import "./ExecutiveNotificationCenter.css";

const POSTURE_META = {
  normal:    { label: "Normal",                   tone: "ok",    icon: ShieldCheck },
  watch:     { label: "Monitor",                  tone: "watch", icon: Activity },
  attention: { label: "Requires Attention",        tone: "warn",  icon: Bell },
  immediate: { label: "Immediate Action Required", tone: "crit",  icon: ShieldAlert },
};

function ageLabel(hours) {
  const h = Number(hours) || 0;
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d`;
}

function fmtIso(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/** Reason-required transition modal (Acknowledge · Resolve · Dismiss). */
function TransitionModal({ open, kind, notification, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  if (!open || !notification) return null;
  const meta = {
    acknowledge: { label: "Acknowledge", verb: "acknowledge", cls: "hw-eap__primary" },
    resolve:     { label: "Resolve",     verb: "resolve",     cls: "hw-eap__primary" },
    dismiss:     { label: "Dismiss",     verb: "dismiss",     cls: "hw-eap__ghost"    },
  }[kind] || {};

  return (
    <div className="hw-eap__modal" role="dialog" aria-modal="true"
          data-testid={`enc-modal-${kind}`}>
      <div className="hw-eap__modal-card">
        <h4>{meta.label}: {notification.title}</h4>
        <p className="hw-eap__modal-help">
          Provide an audit-logged reason ({">="}20 characters).
        </p>
        <textarea
          className="hw-eap__textarea"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Reviewed with security lead, follow-up tracked in incident #482…"
          data-testid={`enc-modal-${kind}-reason`}
        />
        <div className="hw-eap__modal-actions">
          <button type="button" className="hw-eap__ghost"
                  onClick={onClose} disabled={busy}
                  data-testid={`enc-modal-${kind}-cancel`}>
            Cancel
          </button>
          <button
            type="button"
            className={meta.cls || "hw-eap__primary"}
            disabled={busy || reason.trim().length < 20}
            data-testid={`enc-modal-${kind}-submit`}
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit(reason.trim());
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Confirm {meta.verb}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveNotificationCenter() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [categories, setCategories] = useState([]);   // metadata objects
  const [severities, setSeverities] = useState([]);   // list of strings
  const [statuses, setStatuses] = useState([]);
  const [counts, setCounts] = useState(null);
  const [filters, setFilters] = useState({
    category: "all", severity: "all", status: "open", search: "",
  });
  const [modal, setModal] = useState({ open: false, kind: null, n: null });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanReason, setScanReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const qs = new URLSearchParams();
      if (filters.category !== "all") qs.set("category", filters.category);
      if (filters.severity !== "all") qs.set("severity", filters.severity);
      if (filters.status   !== "all") qs.set("status",   filters.status);
      if (filters.search.trim())     qs.set("search",   filters.search.trim());
      qs.set("limit", "200");
      const [listResp, countsResp] = await Promise.all([
        api.get(`/exec-notifications?${qs.toString()}`),
        api.get("/exec-notifications/counts"),
      ]);
      setNotifications(listResp.data.notifications || []);
      setCategories(listResp.data.categories || []);
      setSeverities(listResp.data.severities || []);
      setStatuses(listResp.data.statuses || []);
      setCounts(countsResp.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load notifications"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const posture = POSTURE_META[counts?.posture] || POSTURE_META.normal;
  const PostureIcon = posture.icon;

  // ────────────────────────── Lifecycle helpers ────────────────────────
  const runTransition = useCallback(async (kind, id, reason) => {
    try {
      await api.post(`/exec-notifications/${id}/${kind}`, { reason });
      toast.success(`Notification ${kind}d`);
      setModal({ open: false, kind: null, n: null });
      await load();
    } catch (e) {
      toast.error(formatApiError(e, `Could not ${kind}`));
      throw e;
    }
  }, [load]);

  const runMarkAllRead = async () => {
    if (bulkReason.trim().length < 20) {
      toast.error("Reason must be at least 20 characters");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/exec-notifications/mark-all-read",
                                        { reason: bulkReason.trim() });
      toast.success(`Marked ${data.marked} notifications as acknowledged`);
      setBulkOpen(false); setBulkReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Bulk update failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const runScanNow = async () => {
    if (scanReason.trim().length < 20) {
      toast.error("Reason must be at least 20 characters");
      return;
    }
    setScanBusy(true);
    try {
      const { data } = await api.post("/exec-notifications/scan-now",
                                        { reason: scanReason.trim() });
      toast.success(`Scan complete · ${data.emitted || 0} new notifications`);
      setScanOpen(false); setScanReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Scan failed"));
    } finally {
      setScanBusy(false);
    }
  };

  const heroTiles = useMemo(() => {
    if (!counts) return [];
    return [
      { label: "Open (unread)",      value: counts.total_unread ?? 0 },
      { label: "Critical",            value: counts.critical_unread ?? 0,
        tone: counts.critical_unread > 0 ? "crit" : "ok" },
      { label: "Warning",             value: counts.warning_unread ?? 0,
        tone: counts.warning_unread > 0 ? "warn" : "ok" },
      { label: "Escalated",           value: counts.escalated_open ?? 0,
        tone: counts.escalated_open > 0 ? "warn" : "ok" },
      { label: "Info",                value: counts.info_unread ?? 0 },
    ];
  }, [counts]);

  return (
    <div className="hw-enc" data-testid="executive-notification-center">
      {/* ─────────────── Hero / Health ─────────────── */}
      <div className="hw-enc__hero" data-testid="enc-hero">
        <div className="hw-enc__hero-eyebrow">
          <Bell className="w-3.5 h-3.5"
                 style={{ display: "inline", verticalAlign: "text-bottom",
                          marginRight: "0.35rem" }} />
          Executive Notification Center
        </div>
        <h1>Real-time operational command</h1>
        <p className="hw-enc__hero-sub">
          Curated audit events surface here as executive-priority signals.
          Higher priority = more severe, older, escalated, or higher executive impact.
          Critical items trigger a rate-limited email to the Executive Brief distribution list.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                       gap: "0.6rem", marginBottom: "0.9rem" }}>
          <div
            className={`hw-eap__posture hw-eap__posture--${posture.tone}`}
            data-testid="enc-posture-badge"
            data-posture={counts?.posture || ""}
          >
            <PostureIcon className="w-3.5 h-3.5"
                          style={{ display: "inline",
                                    verticalAlign: "text-bottom",
                                    marginRight: "0.3rem" }} />
            {counts ? posture.label : "…"}
          </div>
        </div>

        <div className="hw-enc__hero-strip">
          {heroTiles.map((t) => (
            <div key={t.label} className="hw-enc__tile"
                  data-testid={`enc-tile-${t.label.toLowerCase().split(" ")[0]}`}>
              <div className="hw-enc__tile-label">{t.label}</div>
              <div className={`hw-enc__tile-value ${t.tone ? `hw-enc__tile-value--${t.tone}` : ""}`}>
                {t.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─────────────── Filters + bulk actions ─────────────── */}
      <div className="hw-enc__toolbar" data-testid="enc-toolbar">
        <select
          className="hw-enc__filter"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          data-testid="enc-filter-category"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        <select
          className="hw-enc__filter"
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          data-testid="enc-filter-severity"
        >
          <option value="all">All severities</option>
          {severities.map((s) => (
            <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        <select
          className="hw-enc__filter"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="enc-filter-status"
        >
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          {statuses.filter((s) => s !== "open").map((s) => (
            <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        <div style={{ position: "relative", flex: "1 1 220px", minWidth: 220 }}>
          <Search className="w-4 h-4"
                   style={{ position: "absolute", top: "50%", left: "0.55rem",
                             transform: "translateY(-50%)", color: "#94A3B8" }} />
          <input
            type="text"
            className="hw-enc__search"
            style={{ paddingLeft: "1.9rem", width: "100%" }}
            placeholder="Search title or body…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="enc-filter-search"
          />
        </div>

        <div className="hw-enc__toolbar-actions">
          <button type="button" className="hw-eap__ghost"
                  onClick={load} disabled={loading}
                  data-testid="enc-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
          <button type="button" className="hw-eap__ghost"
                  onClick={() => setScanOpen(true)}
                  data-testid="enc-scan-now">
            <PlayCircle className="w-3.5 h-3.5" /> Scan now
          </button>
          <button type="button" className="hw-eap__primary"
                  onClick={() => setBulkOpen(true)}
                  disabled={!(counts?.total_unread > 0)}
                  data-testid="enc-mark-all-read">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        </div>
      </div>

      {error && (
        <div className="hw-enc__error" data-testid="enc-error">{error}</div>
      )}

      {/* ─────────────── Notifications list ─────────────── */}
      <div className="hw-enc__card" data-testid="enc-list-card">
        <div className="hw-enc__card-head">
          <div>
            <h3 className="hw-enc__card-title">
              Notifications ({notifications.length})
            </h3>
            <div className="hw-enc__card-sub">
              Sorted by executive priority · severity × 3 + age + impact + escalated
            </div>
          </div>
        </div>

        {loading && notifications.length === 0 ? (
          <div className="hw-enc__empty" data-testid="enc-loading">
            <Loader2 className="w-4 h-4 animate-spin"
                      style={{ display: "inline", verticalAlign: "text-bottom",
                                marginRight: "0.35rem" }} />
            Loading…
          </div>
        ) : notifications.length === 0 ? (
          <div className="hw-enc__empty" data-testid="enc-empty">
            <strong>No notifications match these filters.</strong>
            Try clearing filters, expanding the status, or running a scan.
          </div>
        ) : (
          <ul className="hw-enc__list" data-testid="enc-list">
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onDeepLink={(link) => nav(link)}
                onAction={(kind) => setModal({ open: true, kind, n })}
              />
            ))}
          </ul>
        )}
      </div>

      {/* ─────────────── Lifecycle modal ─────────────── */}
      <TransitionModal
        open={modal.open}
        kind={modal.kind}
        notification={modal.n}
        onClose={() => setModal({ open: false, kind: null, n: null })}
        onSubmit={(reason) => runTransition(modal.kind, modal.n.id, reason)}
      />

      {/* Mark all read modal */}
      {bulkOpen && (
        <div className="hw-eap__modal" role="dialog" aria-modal="true"
              data-testid="enc-modal-mark-all">
          <div className="hw-eap__modal-card">
            <h4>Mark all open as acknowledged</h4>
            <p className="hw-eap__modal-help">
              Applies to every OPEN notification. Provide an audit reason (&ge;20 chars).
            </p>
            <textarea className="hw-eap__textarea" rows={3} maxLength={500}
                       value={bulkReason}
                       onChange={(e) => setBulkReason(e.target.value)}
                       data-testid="enc-modal-mark-all-reason"
                       placeholder="e.g. Post-Monday triage sweep, individual items tracked in Ops backlog…" />
            <div className="hw-eap__modal-actions">
              <button type="button" className="hw-eap__ghost"
                      onClick={() => { setBulkOpen(false); setBulkReason(""); }}
                      disabled={bulkBusy}
                      data-testid="enc-modal-mark-all-cancel">
                Cancel
              </button>
              <button type="button" className="hw-eap__primary"
                      onClick={runMarkAllRead}
                      disabled={bulkBusy || bulkReason.trim().length < 20}
                      data-testid="enc-modal-mark-all-submit">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan now modal */}
      {scanOpen && (
        <div className="hw-eap__modal" role="dialog" aria-modal="true"
              data-testid="enc-modal-scan">
          <div className="hw-eap__modal-card">
            <h4>Run notification scan now</h4>
            <p className="hw-eap__modal-help">
              Forces an immediate re-scan of curated audit events (same code path
              as the 5-minute scheduler). Provide an audit reason (&ge;20 chars).
            </p>
            <textarea className="hw-eap__textarea" rows={3} maxLength={500}
                       value={scanReason}
                       onChange={(e) => setScanReason(e.target.value)}
                       data-testid="enc-modal-scan-reason"
                       placeholder="e.g. Verifying capture after incident #482 remediation window…" />
            <div className="hw-eap__modal-actions">
              <button type="button" className="hw-eap__ghost"
                      onClick={() => { setScanOpen(false); setScanReason(""); }}
                      disabled={scanBusy}
                      data-testid="enc-modal-scan-cancel">
                Cancel
              </button>
              <button type="button" className="hw-eap__primary"
                      onClick={runScanNow}
                      disabled={scanBusy || scanReason.trim().length < 20}
                      data-testid="enc-modal-scan-submit">
                {scanBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Run scan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// One notification row
// ============================================================================
function NotificationRow({ notification: n, onDeepLink, onAction }) {
  const closed = n.status !== "open" && n.status !== "acknowledged";
  return (
    <li className={`hw-enc__item ${closed ? "hw-enc__item--closed" : ""}`}
        data-testid={`enc-item-${n.id}`}>
      <div className="hw-enc__severity">
        <div className={`hw-enc__sev-badge hw-enc__sev-badge--${n.severity}`}
              data-testid={`enc-item-severity-${n.id}`}>
          {n.severity}
        </div>
        <div className="hw-enc__prio-chip" title="Priority score">
          p{Math.round(n.priority || 0)}
        </div>
      </div>

      <div>
        <div className="hw-enc__row">
          <span className="hw-enc__cat">{n.category}</span>
          <span>{ageLabel(n.age_hours)} old</span>
          {n.escalated && (
            <span className="hw-enc__tag hw-enc__tag--esc"
                   data-testid={`enc-item-escalated-${n.id}`}>Escalated</span>
          )}
          <span className={`hw-enc__status ${n.status === "acknowledged" ? "hw-enc__tag--ack" : ""} ${n.status === "resolved" ? "hw-enc__tag--res" : ""} ${n.status === "dismissed" ? "hw-enc__tag--dis" : ""}`}
                 data-testid={`enc-item-status-${n.id}`}>
            {n.status}
          </span>
          {n.email_delivery_status === "delivered" && (
            <span className="hw-enc__tag hw-enc__tag--email">Critical email sent</span>
          )}
          {n.occurrences > 1 && (
            <span title="Total occurrences of this event">
              ×{n.occurrences}
            </span>
          )}
        </div>
        <h4 className="hw-enc__title" data-testid={`enc-item-title-${n.id}`}>
          {n.title}
        </h4>
        <div className="hw-enc__body">{n.body}</div>
        <div className="hw-enc__ctx">
          <span>Created {fmtIso(n.created_at)}</span>
          {n.actor_email && <span>Actor: {n.actor_email}</span>}
          {n.source && <span>Source: <code>{n.source}</code></span>}
          {n.acknowledged_by && (
            <span>Ack&apos;d by {n.acknowledged_by} · {fmtIso(n.acknowledged_at)}</span>
          )}
          {n.resolved_by && (
            <span>Resolved by {n.resolved_by} · {fmtIso(n.resolved_at)}</span>
          )}
        </div>
      </div>

      <div className="hw-enc__cta">
        {n.deep_link && (
          <button type="button" className="hw-eap__ghost"
                  onClick={() => onDeepLink(n.deep_link)}
                  data-testid={`enc-item-deeplink-${n.id}`}>
            <ExternalLink className="w-3.5 h-3.5" /> Open entity
          </button>
        )}
        {n.status === "open" && (
          <button type="button" className="hw-eap__primary"
                  onClick={() => onAction("acknowledge")}
                  data-testid={`enc-item-ack-${n.id}`}>
            <CheckCheck className="w-3.5 h-3.5" /> Acknowledge
          </button>
        )}
        {(n.status === "open" || n.status === "acknowledged") && (
          <button type="button" className="hw-eap__ghost"
                  onClick={() => onAction("resolve")}
                  data-testid={`enc-item-resolve-${n.id}`}>
            <ShieldCheck className="w-3.5 h-3.5" /> Resolve
          </button>
        )}
        {(n.status === "open" || n.status === "acknowledged") && (
          <button type="button" className="hw-eap__ghost"
                  onClick={() => onAction("dismiss")}
                  data-testid={`enc-item-dismiss-${n.id}`}>
            <X className="w-3.5 h-3.5" /> Dismiss
          </button>
        )}
      </div>
    </li>
  );
}
